/**
 * AIReviewButton — the standardized one-click "Review X" AI button.
 *
 * Every surface that works with an AI-writable entity renders this button with
 * the same wording ("Review <Entity>"), the sparkle icon, and the reserved AI
 * purple, so users instantly recognize where the AI can act. The wording,
 * description (tooltip), and canned prompt come from the server catalog (GET
 * /ai/catalog scenarios — see useAICatalog and COMS
 * scenarios/reviewScenarios.ts); reviewActions.ts maps the stable widget key to
 * the scenario name. Clicking it opens the PRATUS AI chat sidebar and starts a
 * new chat with "<slash command> <canned prompt>" prefilled in the input — the
 * slash syntax stays visible as part of the prompt so users get comfortable
 * with it. Nothing is sent automatically — the user can edit the prompt and
 * presses Send themselves. The chat runs in the scenario's mode
 * (`agent_confirm` for reviews), so every proposed write still goes through the
 * normal HITL confirmation flow.
 *
 * While the catalog is loading (or unreachable) the button renders with its
 * fallback label, disabled — it never launches a chat without the real prompt.
 *
 * The button renders only for users holding the "Access AI" (incident:accessAi)
 * permission — the same permission the DGPT chat endpoints enforce server-side
 * — so users who can't get an AI response never see the entry point.
 */
import React from 'react';
import { useDispatch } from 'react-redux';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useChatSidebar } from 'components/GlobalChatSidebar/ChatSidebarProvider';
import { useIncidentPermissions } from 'components/IAP/hooks/incidentHooks';
import { useFeatureFlags } from 'utils/featureFlags';
import { setCurrentSitrepId, setSelectedDChat } from 'slices/dchatSlice';
import {
  AI_REVIEW_SCENARIOS,
  AIReviewActionKey,
  buildScenarioPrefill,
} from './reviewActions';
import { CatalogScenario, useAIScenario } from './hooks/useAICatalog';

/**
 * Launches any catalog scenario: clears stale SITREP context, selects a fresh
 * chat carrying the prefilled prompt (editable in the input by ChatDisasterGPT
 * — not sent), and opens the global chat sidebar. Ui-bearing scenarios keep
 * their visible "<slash command> " prefix.
 */
export const useAIReviewAction = () => {
  const dispatch = useDispatch();
  const { show } = useChatSidebar();

  return (scenario: CatalogScenario) => {
    dispatch(setCurrentSitrepId(undefined));
    dispatch(
      setSelectedDChat({
        name: scenario.ui?.chatName ?? scenario.title,
        mode: scenario.mode,
        initialInput: buildScenarioPrefill(scenario),
      })
    );
    show();
  };
};

type AIReviewButtonProps = {
  actionKey: AIReviewActionKey;
  className?: string;
};

export const AIReviewButton: React.FC<AIReviewButtonProps> = ({
  actionKey,
  className,
}) => {
  const launchReview = useAIReviewAction();
  const { scenarioName, fallbackLabel } = AI_REVIEW_SCENARIOS[actionKey];
  const { scenario } = useAIScenario(scenarioName);
  const { data: incidentPermissions } = useIncidentPermissions();
  const canAccessAi = incidentPermissions?.['incident:accessAi'] ?? false;
  const activeFlags = useFeatureFlags();

  // Same availability rule as the slash palette: a scenario whose
  // server-declared requiredFeatureFlag is inactive (today: reviews QA hasn't
  // verified, stamped IM_SKILLS) doesn't render its one-click button either.
  const featureEnabled =
    !scenario?.requiredFeatureFlag ||
    activeFlags.includes(scenario.requiredFeatureFlag);

  if (!canAccessAi || !featureEnabled) {
    return null;
  }

  return (
    <StylishNewButton
      className={className ?? 'button--ai gap-1'}
      disabled={!scenario}
      title={scenario?.description}
      onClick={(e: React.MouseEvent) => {
        // Buttons live inside collapsible card headers — don't toggle them.
        e.stopPropagation();
        if (scenario) {
          launchReview(scenario);
        }
      }}
    >
      <SharedIcon iconName="auto_awesome" size="sm" />{' '}
      {scenario?.ui?.buttonLabel ?? fallbackLabel}
    </StylishNewButton>
  );
};

export default AIReviewButton;
